import {toggleRefreshSpinner, TokenRefreshLocalKey, TokenRefreshSessionKeys} from 'Core/ducks/refreshToken';
import * as O from 'fp-ts/lib/Option';
import {History} from 'history';
import {getOAuthProviderVariablesExternal, getOAuthProviderVariablesInternal} from 'OAuthProviderVariables';
import {Dispatch} from 'redux';
import {getUserClaims} from '../Shared/utilities/UserProfileUtilities';
import {setSelectedAgency, setUserClaims} from '../Users/ducks/userProfile';
import {fromResult} from './models/Async';
import {updateClaimsInReduxStore} from './sagas/refreshTokenSaga';

/**
 * Determines agent type (Internal or External) and returns respective OAuth Provider Variables
 * @returns OAuthProviderVariables
 */
export const getOAuthVariablesForAgentType = () => {
  const savedEncryptedId = O.fromNullable(sessionStorage.getItem(TokenRefreshSessionKeys.SAVED_ENCRYPTED_ID));
  const isEmployee = O.fromNullable(localStorage.getItem('is_employee'));
  const isamEncryptedIdParam = shouldRefreshToken() && O.isSome(savedEncryptedId) ? savedEncryptedId.value : 'default';
  return O.isSome(isEmployee) && isEmployee.value === 'true'
    ? getOAuthProviderVariablesInternal(isamEncryptedIdParam)
    : getOAuthProviderVariablesExternal(isamEncryptedIdParam);
};

export const shouldRefreshToken = () =>
  !!(
    sessionStorage.getItem(TokenRefreshSessionKeys.TOKEN_REFRESH_FLAG) &&
    sessionStorage.getItem(TokenRefreshSessionKeys.TOKEN_REFRESH_FLAG) === 'true'
  );

export const setTokenRefreshFlag = () => sessionStorage.setItem(TokenRefreshSessionKeys.TOKEN_REFRESH_FLAG, 'true');

export const removeTokenRefreshSessionState = () => {
  sessionStorage.removeItem(TokenRefreshSessionKeys.RE_ROUTE_OPTIONS);
  sessionStorage.removeItem(TokenRefreshSessionKeys.TOKEN_REFRESH_FLAG);
};

export const setRefreshReRouteOptions = (requiresReRoute: boolean, url?: string) =>
  sessionStorage.setItem(
    TokenRefreshSessionKeys.RE_ROUTE_OPTIONS,
    JSON.stringify({
      requiresReRoute,
      route: url,
    })
  );

/**
 * Saves arguments passed to the localStorage
 * @param accessToken the jwt access token
 * @param expiresAt the expiration time for the token * 1000
 * @param updatedClaims the updated claims extracted from the token or fetched from the claimset api
 * @param idToken  the id token
 */
export function updateLocalStorage(accessToken: string, idToken: string, expiresAt: number, updatedClaims: string) {
  localStorage.setItem('access_token', `Bearer ${accessToken}`);
  localStorage.setItem('id_token', idToken);
  localStorage.setItem('expires_at', `${expiresAt * 1000}`);
  localStorage.setItem('claims', updatedClaims);
}

function removeIFrames() {
  const iFrames = Array.from(document.getElementsByTagName('iframe'));
  for (const iFrame of iFrames) {
    iFrame.remove();
  }
}

function route(refreshReRouteOptions: any, history: History, url: string) {
  if (refreshReRouteOptions.requiresReRoute) {
    history.push(url);
  }
}

export const setExpiresAtForAutoRefresh = (dispatch: Dispatch) => {
  const expiresAt = O.fromNullable(localStorage.getItem('expires_at'));
  if (O.isSome(expiresAt)) {
    dispatch(toggleRefreshSpinner(false, '', Number(expiresAt.value)));
  }
};

export const startWindowMessageListener = (dispatch: Dispatch, history: History) => {
  var reloaded = false;
  window.onmessage = (e: any) => {
    if (e.origin === window.location.origin && e.data === 'refreshToken') {
      removeIFrames();
      const isImpersonating = localStorage.getItem(TokenRefreshLocalKey.IN_EMULATION);
      const refreshReRouteOptions = JSON.parse(
        sessionStorage.getItem(TokenRefreshSessionKeys.RE_ROUTE_OPTIONS) || '{}'
      );

      if (!isImpersonating) {
        updateClaimsInReduxStore(dispatch);
        route(refreshReRouteOptions, history, refreshReRouteOptions);
      } else if (isImpersonating && isImpersonating === 'true') {
        updateClaimsInReduxStore(dispatch);
        route(refreshReRouteOptions, history, '/agent/agentDashboard');
      } else if (isImpersonating && isImpersonating === 'false') {
        const newClaims = getUserClaims();
        if (O.isSome(newClaims)) {
          dispatch(setUserClaims(fromResult(newClaims.value)));
          dispatch(setSelectedAgency(O.none, false));
        }
        history.push(refreshReRouteOptions.route);
      }
      removeTokenRefreshSessionState();
      setExpiresAtForAutoRefresh(dispatch);
      window.location.reload();
    }
  };
};
